import {
  AiForecastResponse,
  AiMomentumDashboardResponse,
  HttpResult,
  PopulationPulseDashboardResult,
} from "./types";

export const COHORT_SUPPRESSED_COPY =
  "Keep building momentum. Consistent Face, Voice, and Wearables scans across your organisation help Population Pulse build a clearer picture of population health over time.";

export const AI_MEDICAL_DISCLAIMER =
  "This information is generated by AI and is not medical advice.";

export const PULSE_HERO_TITLE = "PopulationPulse";

export const PULSE_HERO_TAGLINE =
  "The intelligence layer that gives businesses a live, longitudinal view of health across their population. Not engagement metrics. Not utilisation reports. Actual evidence of what is changing and where.";

export function coverageDisclaimer(windowDays: number): string {
  const dayWord = windowDays === 1 ? 'day' : 'days';
  return `For each signal, each day counts how many enrolled members in this group had it, as a share of the group. The number shown is the average of those daily shares over the last ${windowDays} ${dayWord}.`;
}

export function isEmptyOrganisationHealthScore(
  score: number | null | undefined,
): boolean {
  if (score == null || Number.isNaN(Number(score))) {
    return true;
  }

  return Number(score) === 0;
}

/** Empty OHS disclaimer when score is missing or 0 (hourglass on the band gauge). */
export function emptyOrganisationHealthScoreCopy(): string {
  return 'Not enough data to calculate a score. Keep building momentum.';
}

export type OrganisationHealthScoreDay = {
  date: string;
  wellness: number | null;
};

export function parseOrganisationHealthScorePayload(raw: unknown): {
  score: number | null;
  updatedAt: string | null;
  daily: OrganisationHealthScoreDay[];
  contributorCount: number | null;
} {
  if (raw == null || typeof raw !== "object") {
    return { score: null, updatedAt: null, daily: [], contributorCount: null };
  }

  const record = raw as Record<string, unknown>;
  const scoreRaw = record.score ?? record.Score;
  const updatedRaw = record.updatedAt ?? record.UpdatedAt;
  const score = Number(scoreRaw);
  const daily = parseOrganisationHealthScoreDays(record.dailyScores ?? record.DailyScores);
  return {
    score: scoreRaw == null || !Number.isFinite(score) ? null : score,
    updatedAt: toUtcDateOnly(updatedRaw),
    daily,
    contributorCount: latestContributorCount(record.dailyScores ?? record.DailyScores),
  };
}

function parseOrganisationHealthScoreDays(raw: unknown): OrganisationHealthScoreDay[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((item) => {
    if (item == null || typeof item !== "object") {
      return [];
    }

    const record = item as Record<string, unknown>;
    const date = record.date ?? record.Date;
    if (typeof date !== "string" || date.length < 10) {
      return [];
    }

    const scoreRaw = record.score ?? record.Score;
    const score = Number(scoreRaw);
    return [{
      date: date.slice(0, 10),
      wellness: scoreRaw == null || !Number.isFinite(score) ? null : score,
    }];
  });
}

function latestContributorCount(raw: unknown): number | null {
  if (!Array.isArray(raw)) {
    return null;
  }

  for (let index = raw.length - 1; index >= 0; index -= 1) {
    const item = raw[index];
    if (item == null || typeof item !== "object") {
      continue;
    }

    const record = item as Record<string, unknown>;
    const scoreRaw = record.score ?? record.Score;
    const score = Number(scoreRaw);
    if (scoreRaw == null || !Number.isFinite(score)) {
      continue;
    }

    const breakdown = record.scoreBreakdown ?? record.ScoreBreakdown;
    const directRaw = record.contributorCount ?? record.ContributorCount;
    const direct = Number(directRaw);
    if (directRaw != null && Number.isFinite(direct) && direct >= 1) {
      return Math.trunc(direct);
    }

    if (breakdown == null || typeof breakdown !== "object") {
      continue;
    }

    const breakdownRecord = breakdown as Record<string, unknown>;
    const countRaw = breakdownRecord.memberCount ?? breakdownRecord.MemberCount;
    const count = Number(countRaw);
    if (countRaw == null || !Number.isFinite(count) || count < 1) {
      continue;
    }

    return Math.trunc(count);
  }

  return null;
}

export function parseEnrolledMembersCount(raw: unknown): number | null {
  if (raw == null || typeof raw !== "object") {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const value = record.enrolledMembersCount ?? record.EnrolledMembersCount;
  const count = Number(value);
  return value == null || !Number.isFinite(count) ? null : count;
}

export function withOrganisationHealthScore(
  result: PopulationPulseDashboardResult,
  payload: {
    score: number | null;
    updatedAt: string | null;
    daily?: OrganisationHealthScoreDay[];
    contributorCount?: number | null;
  },
): PopulationPulseDashboardResult {
  return {
    ...result,
    organisationHealthScore: payload.score,
    organisationHealthScoreUpdatedAt: payload.updatedAt,
    organisationHealthScoreDaily: payload.daily ?? [],
    organisationHealthScoreContributorCount: payload.contributorCount ?? null,
  };
}

function toUtcDateOnly(value: unknown): string | null {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
}

export function mapPopulationPulseResponses(
  momentum: HttpResult,
  forecast: HttpResult,
): PopulationPulseDashboardResult {
  if (momentum.status === 404 || forecast.status === 404) {
    return { status: "suppressed", message: COHORT_SUPPRESSED_COPY };
  }

  if (
    momentum.status === 502 ||
    momentum.status === 503 ||
    forecast.status === 502 ||
    forecast.status === 503
  ) {
    return {
      status: "unavailable",
      message: firstMessage(
        failingResult(momentum, forecast),
        "Population Pulse is temporarily unavailable. Please try again in a few minutes.",
      ),
    };
  }

  if (momentum.status !== 200 || !isMomentum(momentum.json)) {
    return {
      status: "error",
      message: firstMessage(momentum, "Unable to load Population Pulse."),
    };
  }

  if (forecast.status !== 200 || !isForecast(forecast.json)) {
    return {
      status: "error",
      message: firstMessage(forecast, "Unable to load Population Pulse."),
    };
  }

  return {
    status: "ready",
    momentum: momentum.json,
    forecast: forecast.json,
  };
}

function failingResult(momentum: HttpResult, forecast: HttpResult): HttpResult {
  if (momentum.status === 502 || momentum.status === 503) {
    return momentum;
  }

  return forecast;
}

function firstMessage(result: HttpResult, fallback: string): string {
  if (typeof result.text === "string" && result.text.trim().length > 0) {
    return result.text;
  }

  return fallback;
}

function isMomentum(value: unknown): value is AiMomentumDashboardResponse {
  return (
    isRecord(value) &&
    typeof value.population_momentum_score === "number" &&
    Array.isArray(value.daily)
  );
}

function isForecast(value: unknown): value is AiForecastResponse {
  return isRecord(value) && Array.isArray(value.forecasts);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
